import { useState, useEffect } from 'react';
import { TrendingDown, Star, Zap, ShieldCheck, ChevronDown, ChevronUp, BadgeCheck } from 'lucide-react';
import api from '../utils/api';

const PLAN_COLORS = { basic:'#4DA2FF', standard:'#F5A623', pro:'#FF6B00' };
const PLAN_ICONS = { basic:'🥉', standard:'🥈', pro:'🥇' };

export default function CommissionPage() {
  const [rates, setRates] = useState([]);
  const [plans, setPlans] = useState({});
  const [mySub, setMySub] = useState(null);
  const [tab, setTab] = useState('rates');
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [subscribing, setSubscribing] = useState('');

  useEffect(() => {
    Promise.all([
      api.get('/commission/rates'),
      api.get('/commission/premium/plans'),
      api.get('/commission/premium/my').catch(() => ({ data: { subscription: null } })),
    ]).then(([r, p, m]) => {
      setRates(r.data.rates || []);
      setPlans(p.data.plans || {});
      setMySub(m.data.subscription);
    }).finally(() => setLoading(false));
  }, []);

  const subscribeToPlan = async (planKey) => {
    const txHash = prompt(`Pay monthly fee for ${plans[planKey]?.name} plan ($${plans[planKey]?.monthlyUSD}/month)\n\nPaste your USDT transaction hash after payment:`);
    if (!txHash?.trim()) return;
    setSubscribing(planKey);
    try {
      const res = await api.post('/commission/premium/subscribe', { plan: planKey, txHash, currency: 'USDT' });
      alert(res.data.message);
      setMySub(res.data.subscription);
    } catch (err) { alert(err.response?.data?.message || 'Failed to activate plan'); }
    finally { setSubscribing(''); }
  };

  if (loading) return <div className="flex justify-center items-center min-h-screen"><div className="spinner" /></div>;

  return (
    <div className="min-h-screen pt-20 pb-24 px-4 max-w-3xl mx-auto">
      <div className="mt-4 mb-6">
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Sora' }}>Commission & Fees</h1>
        <p className="text-brand-muted text-sm mt-1">Transparent pricing — lower than Amazon, Temu & eBay</p>
      </div>

      {/* Competitive headline */}
      <div className="rounded-2xl p-4 mb-6" style={{ background: 'linear-gradient(135deg,rgba(255,107,0,0.15),rgba(245,166,35,0.08))', border: '1px solid rgba(255,107,0,0.3)' }}>
        <div className="grid grid-cols-3 gap-3 text-center">
          {[
            { label: 'Our Max Total', value: '18%', color: '#00C864', sub: 'All fees combined' },
            { label: 'Amazon Max', value: '45%', color: '#FF3232', sub: 'Some categories' },
            { label: 'eBay Avg', value: '13-15%', color: '#F5A623', sub: 'Plus listing fees' },
          ].map(({ label, value, color, sub }) => (
            <div key={label}>
              <p className="text-xs text-brand-muted mb-1">{label}</p>
              <p className="text-2xl font-bold" style={{ color, fontFamily: 'Sora' }}>{value}</p>
              <p className="text-xs text-brand-muted">{sub}</p>
            </div>
          ))}
        </div>
        <div className="mt-3 pt-3 flex items-center gap-2 text-xs text-brand-muted" style={{ borderTop: '1px solid rgba(255,107,0,0.2)' }}>
          <ShieldCheck size={13} className="text-green-400" />
          <span>Free shipping subsidy on orders over <strong className="text-white">$30</strong> — CryptoMart absorbs 50% of shipping cut to attract buyers</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 p-1 rounded-xl" style={{ background: '#12121A', border: '1px solid #1E1E2E' }}>
        {[['rates','📊 Category Rates'],['premium','⭐ Premium Plans']].map(([id,label]) => (
          <button key={id} onClick={() => setTab(id)}
            className="flex-1 py-2 rounded-lg text-sm font-semibold transition-all"
            style={{ background: tab===id?'linear-gradient(135deg,#FF6B00,#F5A623)':'transparent', color: tab===id?'white':'#6B6B8A' }}>
            {label}
          </button>
        ))}
      </div>

      {/* RATES TAB */}
      {tab === 'rates' && (
        <div className="space-y-2">
          <div className="grid grid-cols-4 gap-2 px-3 py-2 text-xs text-brand-muted font-semibold">
            <span className="col-span-2">Category</span>
            <span className="text-center">Commission</span>
            <span className="text-center">Total Max</span>
          </div>
          {rates.map(rate => (
            <div key={rate._id} className="rounded-2xl overflow-hidden" style={{ border: '1px solid #1E1E2E' }}>
              <button onClick={() => setExpanded(expanded === rate._id ? null : rate._id)}
                className="w-full grid grid-cols-4 gap-2 px-3 py-3 items-center hover:bg-white/5 transition-colors"
                style={{ background: '#12121A' }}>
                <span className="col-span-2 text-sm font-semibold text-left">{rate.category}</span>
                <span className="text-center font-bold font-mono" style={{ color: '#FF6B00' }}>
                  {rate.minRate}–{rate.maxRate}%
                </span>
                <div className="flex items-center justify-center gap-1">
                  <span className="text-xs font-bold" style={{ color: '#00C864' }}>
                    ~{(rate.defaultRate + rate.shippingCut + 3).toFixed(0)}%
                  </span>
                  {expanded === rate._id ? <ChevronUp size={12} className="text-brand-muted" /> : <ChevronDown size={12} className="text-brand-muted" />}
                </div>
              </button>
              {expanded === rate._id && (
                <div className="px-3 pb-3 pt-1" style={{ background: '#0A0A0F', borderTop: '1px solid #1E1E2E' }}>
                  <p className="text-xs text-brand-muted mb-2">{rate.description}</p>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div className="p-2 rounded-lg" style={{ background: '#12121A' }}>
                      <p className="text-brand-muted">Commission</p>
                      <p className="font-bold" style={{ color: '#FF6B00' }}>{rate.defaultRate}%</p>
                    </div>
                    <div className="p-2 rounded-lg" style={{ background: '#12121A' }}>
                      <p className="text-brand-muted">Shipping cut</p>
                      <p className="font-bold" style={{ color: '#4DA2FF' }}>{rate.shippingCut}%</p>
                    </div>
                    <div className="p-2 rounded-lg" style={{ background: '#12121A' }}>
                      <p className="text-brand-muted">Network fee</p>
                      <p className="font-bold" style={{ color: '#F5A623' }}>3%</p>
                    </div>
                  </div>
                  <div className="mt-2 p-2 rounded-lg text-xs flex items-center gap-2" style={{ background: 'rgba(0,200,100,0.08)', border: '1px solid rgba(0,200,100,0.2)' }}>
                    <TrendingDown size={12} className="text-green-400" />
                    <span className="text-green-300">Orders over $30 get 50% shipping subsidy from CryptoMart — buyer sees "Free Shipping"</span>
                  </div>
                </div>
              )}
            </div>
          ))}
          <div className="p-3 rounded-xl text-xs text-brand-muted mt-2" style={{ background: '#12121A', border: '1px solid #1E1E2E' }}>
            💡 The 1.5% buyer fee shown on product prices is paid by buyers (not sellers). Sellers only pay the commission rates above.
          </div>
        </div>
      )}

      {/* PREMIUM PLANS TAB */}
      {tab === 'premium' && (
        <div className="space-y-4">
          {/* Current subscription */}
          {mySub && (
            <div className="p-4 rounded-2xl" style={{ background: 'rgba(0,200,100,0.08)', border: '1px solid rgba(0,200,100,0.3)' }}>
              <div className="flex items-center gap-2 mb-1">
                <BadgeCheck size={16} className="text-green-400" />
                <span className="font-bold text-sm text-green-300">Active: {mySub.plan?.charAt(0).toUpperCase() + mySub.plan?.slice(1)} Plan</span>
              </div>
              <p className="text-xs text-brand-muted">
                Valid until {mySub.endDate ? new Date(mySub.endDate).toDateString() : 'N/A'}
                {' · '}{mySub.benefits?.reducedCommission}% commission reduction active
              </p>
            </div>
          )}

          {Object.entries(plans).map(([key, plan]) => {
            const color = PLAN_COLORS[key];
            const isActive = mySub?.plan === key;
            return (
              <div key={key} className="rounded-2xl overflow-hidden" style={{ border: `1px solid ${isActive ? color : '#1E1E2E'}` }}>
                <div className="p-4" style={{ background: isActive ? `${color}10` : '#12121A' }}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xl">{PLAN_ICONS[key]}</span>
                        <h3 className="font-bold text-lg" style={{ color, fontFamily: 'Sora' }}>{plan.name}</h3>
                        {isActive && <span className="badge text-xs" style={{ background: `${color}20`, color, border: `1px solid ${color}40` }}>Active</span>}
                      </div>
                      <p className="text-xs text-brand-muted">{plan.description}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold" style={{ color, fontFamily: 'Sora' }}>${plan.monthlyUSD}</p>
                      <p className="text-xs text-brand-muted">per month</p>
                    </div>
                  </div>

                  {/* Benefits */}
                  <div className="space-y-1.5 mb-4">
                    {[
                      [`${plan.benefits.featuredSpots} Featured product slot${plan.benefits.featuredSpots > 1 ? 's' : ''}`, true],
                      [`${plan.benefits.reducedCommission}% commission reduction`, true],
                      ['Priority customer support', plan.benefits.prioritySupport],
                      ['Analytics dashboard', plan.benefits.analyticsAccess],
                      ['Custom store banner', plan.benefits.customStoreBanner],
                    ].map(([benefit, included]) => (
                      <div key={benefit} className="flex items-center gap-2 text-xs">
                        <span style={{ color: included ? '#00C864' : '#6B6B8A' }}>{included ? '✅' : '⬜'}</span>
                        <span style={{ color: included ? '#E0E0F0' : '#6B6B8A' }}>{benefit}</span>
                      </div>
                    ))}
                  </div>

                  {!isActive && (
                    <button onClick={() => subscribeToPlan(key)} disabled={subscribing === key}
                      className="w-full py-2.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all"
                      style={{ background: `linear-gradient(135deg,${color},${color}99)`, color: 'white' }}>
                      {subscribing === key ? <div className="spinner" /> : `Subscribe — $${plan.monthlyUSD}/month`}
                    </button>
                  )}
                  {isActive && (
                    <div className="text-center py-2 text-sm font-bold" style={{ color }}>
                      ✅ Currently Active
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          <div className="p-3 rounded-xl text-xs text-brand-muted" style={{ background: '#12121A', border: '1px solid #1E1E2E' }}>
            💳 Pay subscription fee monthly in USDT. After paying, paste your transaction hash to activate. No auto-renewal unless you re-subscribe.
          </div>
        </div>
      )}
    </div>
  );
}
